import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
// ⚠️ Sous-chemin `/client` OBLIGATOIRE : la racine `@orgazone/auth-web` est le plugin Fastify (argon2,
// fastify-plugin) — l'importer ici ferait entrer du code serveur dans le bundle navigateur.
import { resolveSession, type AuthMode } from '@orgazone/auth-web/client';
import { showToast as pushToast, useWebTheme } from '@orgazone/ui-web';
import { api, type SessionUser } from './api';

// État applicatif transverse de la console : SESSION RÉELLE (mode d'auth + utilisateur connecté) + thème/
// densité + toast + point de contrôle unique des actions mutantes. ⚠️ Fin de la démo : le rôle et l'user ne
// sont plus des littéraux — ils viennent du socle d'auth (/v1/auth/me).
//
// ⚠️ CE QUI N'EST PLUS ICI : l'état de chargement des écrans. Il vivait dans ce contexte, donc UNE SEULE
// variable pour onze écrans — la réponse d'un écran quitté venait affirmer un échec sur le suivant. Il
// appartient désormais à chaque écran (`useResource`), qui est le seul à savoir ce qu'il a chargé.

export type Theme = 'light' | 'dark';
export type Density = 'compact' | 'normal' | 'comfortable';
export type Tone = 'ok' | 'warn' | 'err' | 'info';

export interface ToastMsg { msg: string; tone: Tone }

interface AppCtx {
  // ── Auth ──
  authReady: boolean;              // la session a été résolue au moins une fois (évite un flash de login)
  authMode: AuthMode;              // { oidc, armed } lu de /v1/auth/mode
  authError: string | null;        // mode d'auth INDÉTERMINÉ (service injoignable) — dit, jamais masqué
  sessionUser: SessionUser | null; // utilisateur connecté (null si non connecté)
  admin: boolean;                  // capacité d'ÉCRITURE : rôle « admin » requis
  user: { name: string; initials: string; role: string }; // pour l'affichage (topbar/rail)
  refreshSession: () => Promise<void>;
  logout: () => Promise<void>;
  // ── UI ──
  theme: Theme; toggleTheme: () => void;
  density: Density; setDensity: (d: Density) => void;
  showToast: (msg: string, tone?: Tone) => void;
  /** Point de contrôle UNIQUE des actions mutantes : refuse + explique en lecture seule. */
  guard: () => boolean;
}

const Ctx = createContext<AppCtx | null>(null);

/** Initiales d'affichage à partir d'un nom (2 premières lettres des 2 premiers mots). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const s = parts.slice(0, 2).map((w) => w[0]).join('');
  return (s || '—').toUpperCase();
}

export function AppProvider({ children }: { children: ReactNode }) {
  // Thème : mécanique PARTAGÉE `useWebTheme` (§24.9sexies-ter d) — apporte la PERSISTANCE qui manquait
  // (l'ancien état local repartait en clair à chaque rechargement, relevé à l'audit).
  const { mode: theme, toggle: toggleTheme } = useWebTheme('oz-edi-theme', 'light');
  const [density, setDensity] = useState<Density>('compact');

  const [authReady, setAuthReady] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>({ oidc: false, armed: false });
  const [authError, setAuthError] = useState<string | null>(null);
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);

  // Résout la session au boot. La RÈGLE (fail-closed si le mode est illisible, 401 = simplement pas
  // connecté, toute autre panne = état DIT et non masqué) vit dans @orgazone/auth-web/client : elle est
  // identique store/console/edi et testée là-bas — ici on ne fait que la brancher sur notre état React.
  const refreshSession = useCallback(async () => {
    const s = await resolveSession<SessionUser>({
      fetchMode: () => api.authMode(),
      fetchUser: async () => (await api.me()).user,
    });
    setAuthMode(s.mode);
    setSessionUser(s.user);
    setAuthError(s.error);
    setAuthReady(true);
  }, []);
  useEffect(() => { void refreshSession(); }, [refreshSession]);

  // Termine la session serveur puis renvoie au login (best-effort : on quitte localement même si l'appel échoue).
  const logout = useCallback(async () => {
    try { await api.logout(); } catch { /* best-effort */ }
    setSessionUser(null);
    window.location.assign('/login');
  }, []);

  // Densité sur la racine du document (le thème, lui, est posé par useWebTheme).
  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  // ⚠️ DÉLÈGUE au rendu partagé (@orgazone/ui-web) : file d'attente, empilement et durée y vivent, lus
  // de la spécification commune. L'ancien état local n'affichait qu'UN toast et le retirait en comparant
  // le TEXTE — deux messages identiques se neutralisaient donc l'un l'autre.
  const showToast = useCallback((msg: string, tone: Tone = 'ok') => pushToast(msg, tone), []);

  // Capacité d'écriture : rôle « admin ». ⚠️ FAIL-CLOSED — sans session, aucune écriture. Ce n'est qu'un
  // confort d'affichage : la vraie décision est prise par la garde serveur (403 sur `edi.write`), qui est
  // inconditionnelle depuis 2026-08-08. Cet écran ne fait qu'éviter de proposer ce qui sera refusé.
  const admin = sessionUser?.roles.includes('admin') ?? false;

  const guard = useCallback(() => {
    if (admin) return true;
    showToast('Action indisponible en lecture seule', 'warn');
    return false;
  }, [admin, showToast]);

  const user = useMemo(() => {
    const name = sessionUser?.name ?? 'Non connecté';
    const role = sessionUser?.roles[0] ?? '';
    return { name, initials: initialsOf(name), role };
  }, [sessionUser]);

  const value = useMemo<AppCtx>(() => ({
    authReady, authMode, authError, sessionUser, admin, user, refreshSession, logout,
    theme, toggleTheme,
    density, setDensity,
    showToast, guard,
  }), [authReady, authMode, authError, sessionUser, admin, user, refreshSession, logout, theme, toggleTheme, density, showToast, guard]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp hors AppProvider');
  return v;
}
